import { ChangeDetectionStrategy, Component, effect, inject, signal, untracked, viewChild } from '@angular/core';
import { NavController } from '@ionic/angular/standalone';

import { ToastController } from 'src/app/core/controllers/toast';
import { AnalyticsService } from 'src/app/core/services/analytics';
import { UserInitService } from 'src/app/core/services/user-init.service';
import { DOMAIN_JOIN_ROLE_NAME, DomainJoinService, domainJoinMessage } from 'src/app/domain/domain-join';
import { AccountState } from 'src/app/store/account';
import { DomainJoinState, type DomainJoinOffer } from 'src/app/store/domain-join';
import { RouterState } from 'src/app/store/router';
import { ButtonComponent } from 'src/app/ui-kit/button/button.component';
import { ResponsiveModalComponent } from 'src/app/ui-kit/responsive-modal/responsive-modal.component';

/** Screens the prompt never covers: start-up, signing in or up, and verification. */
const QUIET_ROUTES = [
  '/loading',
  '/sign-in',
  '/sign-up',
  '/setup-profile',
  '/complete-profile',
  '/invite',
  '/auth-actions',
  '/forgot-password',
  '/account/verify-email',
  '/account/enter-verification-code',
];

/**
 * The prompt after sign-in (or app load) for domain-based invitations: each
 * district the person may join from their verified email domain, with Accept.
 *
 * There is no decline. "Not now" only closes the prompt; the same offers stay
 * on My Blume, and the prompt returns on the next app load.
 *
 * Also the trigger for the offer check: it watches the signed-in account and
 * its email verification, so the check runs on sign-in and again the moment a
 * new account verifies its email, with no refresh. Shown once per app load,
 * after start-up and never over the sign-in, sign-up or verification screens.
 *
 * @feature layout
 */
@Component({
  selector: 'app-domain-join-prompt',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ResponsiveModalComponent, ButtonComponent],
  template: `
    <app-responsive-modal #modal title="Join your team" [autoHeight]="true" (onDismiss)="onDismissed()">
      <div class="space-y-4 py-2">
        <p class="text-body leading-relaxed">
          Your email address is part of
          {{ offers().length === 1 ? 'a team' : 'these teams' }} on Blume. Join to start working with your colleagues.
        </p>
        <ul class="space-y-3" aria-label="Teams you can join">
          @for (offer of offers(); track offer.partnerId) {
            <li
              class="rounded-2xl border border-outline bg-surface p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3"
            >
              <div class="min-w-0">
                <div class="font-semibold text-base">{{ offer.partnerName }}</div>
                <div class="text-sm text-muted" aria-live="polite">
                  {{ accepting() === offer.partnerId ? 'Joining your team...' : "You'll join as a " + roleName }}
                </div>
              </div>
              <app-button
                color="purple"
                fill="solid"
                size="small"
                [disabled]="accepting() !== null"
                [loading]="accepting() === offer.partnerId"
                [ariaLabel]="'Join ' + offer.partnerName"
                (click)="accept(offer)"
              >
                Accept
              </app-button>
            </li>
          }
        </ul>
        <div class="flex justify-end pt-1">
          <app-button color="dark" fill="clear" size="small" [disabled]="accepting() !== null" (click)="notNow()">
            Not now
          </app-button>
        </div>
      </div>
    </app-responsive-modal>
  `,
})
export class DomainJoinPromptComponent {
  private readonly state = inject(DomainJoinState);
  private readonly service = inject(DomainJoinService);
  private readonly accountState = inject(AccountState);
  private readonly routerState = inject(RouterState);
  private readonly userInit = inject(UserInitService);
  private readonly analytics = inject(AnalyticsService);
  private readonly toast = inject(ToastController);
  private readonly navController = inject(NavController);

  private readonly modal = viewChild.required<ResponsiveModalComponent>('modal');

  protected readonly offers = this.state.offers;
  /** The district being joined, or null. */
  protected readonly accepting = signal<string | null>(null);
  /** Shown already in this app load (for this person). */
  /** How the role a domain-based join grants is shown ("User"). */
  protected readonly roleName = DOMAIN_JOIN_ROLE_NAME;
  private shown = false;
  /** Set when the popup closes because the last offer was joined, so that close is not a dismissal. */
  private closingAfterJoin = false;

  constructor() {
    // Sign-out: forget the offers, so the next person (or the same person
    // signing back in) is checked afresh and prompted again.
    effect(() => {
      if (!this.accountState.email()) {
        untracked(() => {
          this.shown = false;
          this.state.reset();
        });
      }
    });

    // Check for offers whenever the signed-in account, or its verification,
    // changes: on sign-in, and the moment a new account verifies its email.
    effect(() => {
      const email = this.accountState.email();
      const verifiedAt = this.accountState.emailVerifiedAt();
      if (!email || !this.accountState.initialized()) return;
      untracked(() => void this.state.load(email, verifiedAt));
    });

    // Open once per app load, as soon as there are offers AND start-up has
    // finished, and not over a sign-in or verification screen. Never before
    // the person has accepted the current terms: until then My Blume shows
    // the consent card, and the prompt opens the moment they accept.
    effect(() => {
      const offers = this.offers();
      const termsAccepted = this.accountState.hasAcceptedCurrentTerms();
      const url = this.routerState.currentUrl() ?? '';
      const quiet = QUIET_ROUTES.some(
        route => url === route || url.startsWith(`${route}/`) || url.startsWith(`${route}?`),
      );
      if (this.shown || !offers.length || !termsAccepted || !this.userInit.complete() || quiet) return;
      untracked(() => this.show(offers));
    });
  }

  protected async accept(offer: DomainJoinOffer): Promise<void> {
    if (this.accepting()) return;
    this.accepting.set(offer.partnerId);
    try {
      const result = await this.service.accept(offer, 'prompt');
      // The team is loaded and switched in by now, so everything changes in
      // one step: the popup closes, the person lands on My Blume in the new
      // district (no navigation when they are already there), then the toast.
      if (!this.offers().length) {
        this.closingAfterJoin = true;
        this.modal().close();
      }
      if (!this.onMyBlume()) void this.navController.navigateRoot(['/my-blume']);
      await this.toast.success(domainJoinMessage(offer.partnerName, result));
    } catch (error) {
      if (!this.offers().length) this.modal().close();
      await this.toast.error(error instanceof Error ? error.message : 'Could not join this team.', {
        errorCode: 'domain_join_failed',
      });
    } finally {
      this.accepting.set(null);
    }
  }

  /** True when the current page is My Blume, so landing there needs no navigation. */
  private onMyBlume(): boolean {
    const url = this.routerState.currentUrl() ?? '';
    return url === '/my-blume' || url.startsWith('/my-blume?') || url.startsWith('/my-blume/');
  }

  /**
   * The popup closed. Unless it closed because the last offer was joined,
   * the person dismissed it (Not now, the backdrop, a swipe): record that for
   * each district still on offer. The offers stay on My Blume.
   */
  protected onDismissed(): void {
    if (this.closingAfterJoin) {
      this.closingAfterJoin = false;
      return;
    }
    const offers = this.offers();
    for (const offer of offers) {
      this.analytics.trackFor(offer.partnerId, 'Domain Invitation Dismissed', { offer_count: offers.length });
    }
  }

  protected notNow(): void {
    this.modal().close();
  }

  private show(offers: readonly DomainJoinOffer[]): void {
    this.shown = true;
    this.modal().open();
    for (const offer of offers) {
      // Filed under that district only if they join it within a minute.
      this.analytics.trackFor(offer.partnerId, 'Domain Invitation Offered');
    }
  }
}
